import React, { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { getHhrgCosts, updateHhrgCosts } from '../../services/hhrgCostsApi';
import type { AgencyClinicResponse } from '../../services/clinicsApi';

const DISCIPLINES = ['SN', 'PT', 'OT', 'ST', 'MSW', 'HHA'] as const;
type Discipline = (typeof DISCIPLINES)[number];

type Props = { clinics: AgencyClinicResponse[] };
type CostRow = Record<Discipline, string>;

const emptyRows = (): CostRow =>
  Object.fromEntries(
    DISCIPLINES.map(discipline => [discipline, ''])
  ) as CostRow;

const validMoneyInput = (value: string) =>
  /^(?:\d+(?:\.\d{0,2})?)?$/.test(value);
const validMoney = (value: string) => /^\d+(?:\.\d{1,2})?$|^\d+$/.test(value);

const HhrgCostsSection: React.FC<Props> = ({ clinics }) => {
  const { canEdit } = useAdminSectionAccess('hhrg-costs');
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const [clinicId, setClinicId] = useState(clinics[0]?.id ?? '');
  const [rows, setRows] = useState<CostRow>(emptyRows);
  const [loading, setLoading] = useState(false);
  const [settledClinicId, setSettledClinicId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clinics.some(clinic => clinic.id === clinicId))
      setClinicId(clinics[0]?.id ?? '');
  }, [clinics, clinicId]);

  useEffect(() => {
    if (!clinicId) return;
    let active = true;
    setRows(emptyRows());
    setLoading(true);
    getHhrgCosts({ path: { agency_clinic_id: clinicId } })
      .then(response => {
        if (!active) return;
        if (response.error) {
          setRows(emptyRows());
          throw new Error('HHRG costs request failed');
        }
        const next = emptyRows();
        for (const item of response.data?.defaults ?? []) {
          if (item.discipline in next)
            next[item.discipline as Discipline] = String(item.cost_per_visit);
        }
        setRows(next);
      })
      .catch(error => {
        if (!active) return;
        setRows(emptyRows());
        console.error('Failed to load HHRG costs:', error);
        showError('Unable to load HHRG staff costs. Please try again.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setSettledClinicId(clinicId);
      });
    return () => {
      active = false;
    };
  }, [clinicId, showError]);

  const save = async () => {
    if (!clinicId) return;
    if (
      DISCIPLINES.some(discipline => {
        const value = rows[discipline].trim();
        return value !== '' && !validMoney(value);
      })
    ) {
      showError('Enter a nonnegative cost with no more than 2 decimal places.');
      return;
    }
    setSaving(true);
    try {
      const response = await updateHhrgCosts({
        path: { agency_clinic_id: clinicId },
        body: {
          defaults: DISCIPLINES.filter(d => rows[d].trim() !== '').map(d => ({
            discipline: d,
            cost_per_visit: rows[d],
          })),
        },
      });
      if (response.error) throw new Error('HHRG costs request failed');
      showSuccess('HHRG staff costs saved');
    } catch (error) {
      console.error('Failed to save HHRG costs:', error);
      showError('Unable to save HHRG staff costs. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-5'>
      <div>
        <h2 className='text-xl font-semibold'>HHRG staff costs</h2>
        <p className='text-sm text-muted-foreground'>
          Estimated staff cost per visit. These settings do not change claim
          charges or reimbursement.
        </p>
      </div>
      <div className='w-full max-w-sm'>
        <label htmlFor='hhrg-clinic' className='mb-2 block text-sm font-medium'>
          Clinic
        </label>
        <Select value={clinicId} onValueChange={setClinicId}>
          <SelectTrigger id='hhrg-clinic'>
            <SelectValue placeholder='Select a clinic' />
          </SelectTrigger>
          <SelectContent>
            {clinics.map(clinic => (
              <SelectItem key={clinic.id} value={clinic.id}>
                {clinic.name || 'Unnamed clinic'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {clinicId && (loading || settledClinicId !== clinicId) ? (
        <Loader2
          className='h-5 w-5 animate-spin'
          aria-label='Loading HHRG costs'
        />
      ) : (
        <div className='overflow-x-auto rounded-md border'>
          <Table className='min-w-[420px]'>
            <TableHeader>
              <TableRow>
                <TableHead>Discipline</TableHead>
                <TableHead>Cost per visit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {DISCIPLINES.map(discipline => (
                <TableRow key={discipline}>
                  <TableCell className='font-medium'>{discipline}</TableCell>
                  <TableCell>
                    <Input
                      inputSize='sm'
                      inputMode='decimal'
                      aria-label={`${discipline} cost per visit`}
                      placeholder='Not set'
                      value={rows[discipline]}
                      disabled={!canEdit}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        const value = event.target.value;
                        if (validMoneyInput(value))
                          setRows(previous => ({
                            ...previous,
                            [discipline]: value,
                          }));
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {canEdit && (
        <Button onClick={save} disabled={saving || loading || !clinicId}>
          {saving ? 'Saving…' : 'Save costs'}
        </Button>
      )}
    </div>
  );
};

export default HhrgCostsSection;
